import React, { useState } from 'react';
import { Copy, Check, ExternalLink, AlertTriangle, BarChart3, Users, Zap, ChevronDown, ChevronUp, PlayCircle, Loader } from 'lucide-react';
import styles from './NotificationContainer.module.sass';
import { ProBadgeSmall, ProSection } from 'utils/ProBadge';

const formatValue = (value) => {
  if (!value) return '0.00';
  
  const match = String(value).match(/^([\d.]+)\s*(.*)$/);
  if (match) {
    // Only return the numeric part
    const [_, number, unit] = match;
    return `${parseFloat(number).toFixed(2)}`;
  }
  return value;
};

/**
 * Gets the position for the gauge indicator (0-100%)
 */
const getRelativePosition = (current, average) => {
  const currentVal = parseFloat(current);
  const avgVal = parseFloat(average);
  
  if (!avgVal) return 50; // If no average, position in the middle
  
  const ratio = currentVal / avgVal;
  
  // Use a logarithmic scale for better visualization
  // 0.25x avg = 0%, 1x avg = 50%, 4x avg = 100%
  const logRatio = Math.log2(ratio) + 2; // +2 shifts the range to start from 0 (for 0.25x)
  
  // Clamp between 0 and 4 (represents 0.25x to 4x average)
  const clampedLogRatio = Math.max(0, Math.min(4, logRatio));
  
  // Convert to percentage
  return (clampedLogRatio / 4) * 100;
};

/**
 * Gets gauge color based on value compared to average
 */
const getGaugeColor = (current, average) => {
  if (!current || !average) return '#4B5563'; // Default gray
  
  const currentVal = parseFloat(current);
  const avgVal = parseFloat(average);
  
  if (currentVal < avgVal) return '#3B82F6'; // Blue for below average
  if (currentVal < avgVal * 1.3) return '#10B981'; // Green for near/slightly above average
  return '#F59E0B'; // Amber for significantly above average
};

/**
 * Gets the trade ratio text
 */
const getTradeRatioText = (current, average) => {
  const currentVal = parseFloat(current);
  const avgVal = parseFloat(average);
  
  if (!avgVal) return "n/a";
  
  const ratio = (currentVal / avgVal).toFixed(2);
  return `${ratio}× avg`;
};

// Define animations
const spin = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const fadeIn = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const NotificationContent = ({ notification, membershipStatus, isToast }) => {
  const [activeTab, setActiveTab] = useState('addresses');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [copiedState, setCopiedState] = useState({
    from: false,
    to: false,
    tx: false,
    token: false
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [deepAnalysisData, setDeepAnalysisData] = useState(null);
  
  const handleCopy = (text, type, e) => {
    e.stopPropagation();
    if (!text) return;
    
    navigator.clipboard.writeText(text);
    setCopiedState(prev => ({ ...prev, [type]: true }));
    
    setTimeout(() => {
      setCopiedState(prev => ({ ...prev, [type]: false }));
    }, 2000);
  };

  const gaugeColor = getGaugeColor(notification.display_value, notification.historical_avg);

  // const handleUpgrade = () => {
  //   // Handle upgrade logic here
  //   // You could redirect to pricing page or open a modal
  // };
  
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  
  // Function to format date from timestamp
  const formatDate = (timestamp) => {
    try {
      return new Date(parseInt(timestamp) * 1000).toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  // New function to handle deep analysis using token transfer data
  const runDeepAnalysis = async () => {
    setIsAnalyzing(true);
    
    try {
      // Query the transfer data for this specific token
      // In a real implementation, you would call your API:
      // const data = await getTransfers(auth.user.access_token, [notification.token_address]);
      
      // Simulating API call with timeout
      setTimeout(() => {
        // Mock transfer data for this token
        const transfers = Array(Math.floor(Math.random() * 15) + 5).fill(0).map((_, i) => {
          const timestamp = Math.floor(Date.now() / 1000) - (i * 86400 * Math.random() * 5);
          const value = (Math.random() * 100).toFixed(2);
          return {
            from_address: `0x${Math.random().toString(16).substring(2, 12)}...`,
            to_address: `0x${Math.random().toString(16).substring(2, 12)}...`,
            value,
            timestamp: timestamp.toString(),
            transaction_hash: `0x${Math.random().toString(16).substring(2, 42)}`
          };
        });
        
        // Sort by timestamp, newest first
        const sortedTransfers = [...transfers].sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp));
        
        // Calculate metrics
        const totalVolume = transfers.reduce((sum, t) => sum + parseFloat(t.value), 0);
        const uniqueAddresses = new Set([
          ...transfers.map(t => t.from_address),
          ...transfers.map(t => t.to_address)
        ]);
        
        const latestTransfer = sortedTransfers[0];
        const oldestTransfer = sortedTransfers[sortedTransfers.length - 1];
        
        // Create analysis data object
        const analysisData = {
          tokenMetrics: {
            totalTransfers: transfers.length,
            totalVolume: totalVolume.toFixed(2),
            uniqueAddresses: uniqueAddresses.size,
            transferPeriod: `${formatDate(oldestTransfer.timestamp)} - ${formatDate(latestTransfer.timestamp)}`
          },
          latestTransfer: {
            from: latestTransfer.from_address,
            to: latestTransfer.to_address,
            value: latestTransfer.value,
            timestamp: formatDate(latestTransfer.timestamp),
            hash: latestTransfer.transaction_hash
          },
          marketContext: {
            // Additional market context data
            volumeChange: ((Math.random() * 40) - 20).toFixed(2),
            priceImpact: (Math.random() * 0.05).toFixed(4),
            liquidityRatio: (Math.random() * 10).toFixed(2)
          }
        };
        
        setDeepAnalysisData(analysisData);
        setIsAnalyzing(false);
      }, 2000);
    } catch (error) {
      console.error("Error running deep analysis");
      setIsAnalyzing(false);
    }
  };
  
  // Get value for typography display
  return (
    <div className={`${styles.card} ${notification.is_anomalous ? styles.anomalous : styles.normal}`}>
      {/* Summary Header */}
      <div className={styles.cardHeader}>
        <div className={styles.tokenInfo}>
          <div className={styles.tokenSymbol}>{notification.token_symbol || 'ETH'}</div>
          <div className={styles.tokenName}>{notification.token_name || 'Ethereum'}</div>
        </div>
        
        <div className={styles.valueDisplay}>
          <div className={styles.valueTypography}>
            {formatValue(notification.display_value)}
          </div>
          <div className={styles.timestamp}>
            {notification.timestamp ? new Date(notification.timestamp * 1000).toLocaleString() : 'Just now'}
          </div>
        </div>
        
        <div className={styles.statusContainer}>
          <div className={styles.statusWrapper}>
            <div className={styles.statusBadgeContainer}>
              <span className={`${styles.statusBadge} ${notification.is_anomalous ? styles.anomalous : styles.normal}`}>
                {notification.anomaly_type || (notification.is_anomalous ? 'Anomalous' : 'Normal')}
              </span>
            </div>
            <button 
              className={`${styles.dropdownArrow} ${isDropdownOpen ? styles.active : ''}`} 
              onClick={toggleDropdown}
              aria-label="Toggle details"
            >
              {isDropdownOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Tab Navigation */}
      {isDropdownOpen && (
        <div className={styles.tabNavigation}>
          <button 
            className={`${styles.tabButton} ${activeTab === 'addresses' ? styles.active : ''}`} 
            onClick={() => setActiveTab('addresses')}
          >
            <Users size={14} />
            <span>Details</span>
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'comparison' ? styles.active : ''}`} 
            onClick={() => setActiveTab('comparison')}
          >
            <BarChart3 size={14} />
            <span>Comparison</span>
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'impact' ? styles.active : ''}`} 
            onClick={() => setActiveTab('impact')}
          >
            <Zap size={14} />
            <span>Impact</span>
            <ProBadgeSmall />
          </button>
        </div>
      )}
      
      {/* Tab Content */}
      {isDropdownOpen && (
        <div className={styles.tabContent}>
          {activeTab === 'comparison' && (
            <div className={styles.comparisonTab}>
              <div className={styles.compactGauge}>
                <div className={styles.gaugeTrack}>
                  <div className={styles.gaugeLabels}>
                    <div className={styles.gaugeRangeLabel}>SMALL</div>
                    <div className={styles.gaugeRangeLabel}>AVERAGE</div>
                    <div className={styles.gaugeRangeLabel}>LARGE</div>
                  </div>
                  <div 
                    className={styles.gaugeIndicator} 
                    style={{
                      left: `${getRelativePosition(notification.value, notification.historical_avg)}%`,
                      background: `linear-gradient(to bottom, ${gaugeColor}, ${gaugeColor}D0)`,
                    }}
                  />
                  <div className={styles.gaugeMidpoint} />
                </div>
                
                {/* Compact metrics display */}
                <div className={styles.compactMetricsContainer}>
                  <div className={styles.compactMetricRow}>
                    <span className={styles.compactMetricLabel}>Historical Avg:</span>
                    <span className={styles.compactMetricValue}>{parseFloat(notification.historical_avg).toFixed(2)}</span>
                    <span className={styles.compactMetricLabel}>Ratio:</span>
                    <span className={styles.compactMetricValue}>{getTradeRatioText(notification.value, notification.historical_avg)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'addresses' && (
            <div className={styles.addressesTab}>
              <div className={styles.addressBlock}>
                <div className={styles.addressLabel}>From Address</div>
                <div className={styles.addressValue}>
                  <span className={styles.fullAddress}>{notification.from_address}</span>
                  <button 
                    onClick={(e) => handleCopy(notification.from_address, 'from', e)}
                    className={styles.copyButton}
                  >
                    {copiedState.from ? <Check size={12} /> : <Copy size={12} />}
                  </button>
                </div>
              </div>
              
              <div className={styles.addressBlock}>
                <div className={styles.addressLabel}>To Address</div>
                <div className={styles.addressValue}>
                  <span className={styles.fullAddress}>{notification.to_address}</span>
                  <button 
                    onClick={(e) => handleCopy(notification.to_address, 'to', e)}
                    className={styles.copyButton}
                  >
                    {copiedState.to ? <Check size={12} /> : <Copy size={12} />}
                  </button>
                </div>
              </div>
              
              {/* Token Address - moved from comparison tab to details tab */}
              <div className={styles.addressBlock}>
                <div className={styles.addressLabel}>Token Address</div>
                <div className={styles.addressValue}>
                  <span className={styles.fullAddress}>{notification.token_address}</span>
                  <button 
                    onClick={(e) => handleCopy(notification.token_address, 'token', e)}
                    className={styles.copyButton}
                  >
                    {copiedState.token ? <Check size={12} /> : <Copy size={12} />}
                  </button>
                </div>
              </div>
              
              {notification.transaction_hash && (
                <div className={styles.transactionLinkContainer}>
                  <a 
                    href={`https://etherscan.io/tx/${notification.transaction_hash}`}
                    className={styles.transactionBadge}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink size={14} />
                    <span>View Transaction</span>
                  </a>
                </div>
              )}
            </div>
          )}

          {activeTab === 'impact' && (
            // <ProSection membershipStatus={membershipStatus} onUpgrade={handleUpgrade}>
              <div className={styles.impactTab}>
                <div className={styles.impactMetrics}>
                  <div className={styles.impactMetricItem}>
                    <div className={styles.impactMetricTitle}>Market Impact</div>
                    <div className={styles.impactMetricValue}>
                      {notification.market_impact ? 
                        `${(notification.market_impact * 100).toFixed(2)}%` : 
                        'Low'
                      }
                    </div>
                    <div className={styles.impactMetricDescription}>
                      Estimated price impact on token liquidity
                    </div>
                  </div>
                  
                  <div className={styles.impactMetricItem}>
                    <div className={styles.impactMetricTitle}>Wallet Value</div>
                    <div className={styles.impactMetricValue}>
                      {notification.wallet_value ? 
                        `$${parseInt(notification.wallet_value).toLocaleString()}` : 
                        '$1.2M'
                      }
                    </div>
                    <div className={styles.impactMetricDescription}>
                      Total wallet holdings value
                    </div>
                  </div>
                  
                  <div className={styles.impactMetricItem}>
                    <div className={styles.impactMetricTitle}>Holder Rank</div>
                    <div className={styles.impactMetricValue}>
                      {notification.holder_rank ? 
                        `#${notification.holder_rank}` : 
                        'Top 1%'
                      }
                    </div>
                    <div className={styles.impactMetricDescription}>
                      Ranking among all token holders
                    </div>
                  </div>
                </div>
                
                <div className={styles.impactAnalysis}>
                  {/* Deep Analysis Button */}
                  <div className={styles.deepAnalysisSection}>
                    <button 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '13px',
                        border: 'none',
                        background: 'linear-gradient(45deg, #3B82F6, #60A5FA)',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        marginTop: '12px',
                        width: '100%',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                      }}
                      className={`${styles.deepAnalysisButton} ${isAnalyzing ? styles.analyzing : ''}`}
                      onClick={runDeepAnalysis}
                      disabled={isAnalyzing}
                    >
                      {isAnalyzing ? (
                        <>
                          <Loader size={14} className={styles.loadingIcon} style={{ animation: 'spin 1s linear infinite' }} />
                          <span>Analyzing...</span>
                        </>
                      ) : (
                        <>
                          <PlayCircle size={14} />
                          <span>Run Deep Analysis</span>
                        </>
                      )}
                    </button>
                    
                    {deepAnalysisData && (
                      <div className={styles.deepAnalysisResults} style={{
                        marginTop: '16px',
                        borderRadius: '8px',
                        background: 'rgba(59, 130, 246, 0.08)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        animation: 'fadeIn 0.3s ease'
                      }}>
                        {/* Transfer Summary Section */}
                        <div style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          background: 'rgba(0, 0, 0, 0.2)',
                          padding: '10px 12px'
                        }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: 'rgba(255, 255, 255, 0.9)',
                            marginBottom: '8px'
                          }}>Transfer Summary</div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '8px'
                          }}>
                            <div style={{
                              background: 'rgba(17, 17, 23, 0.8)',
                              padding: '8px',
                              borderRadius: '6px',
                              textAlign: 'center'
                            }}>
                              <div style={{
                                fontSize: '15px',
                                fontWeight: '700',
                                color: 'white'
                              }}>{deepAnalysisData.tokenMetrics.totalTransfers}</div>
                              <div style={{
                                fontSize: '10px',
                                color: 'rgba(255, 255, 255, 0.6)'
                              }}>Transfers</div>
                            </div>
                            
                            <div style={{
                              background: 'rgba(17, 17, 23, 0.8)',
                              padding: '8px',
                              borderRadius: '6px',
                              textAlign: 'center'
                            }}>
                              <div style={{
                                fontSize: '15px',
                                fontWeight: '700',
                                color: 'white'
                              }}>{deepAnalysisData.tokenMetrics.totalVolume}</div>
                              <div style={{
                                fontSize: '10px',
                                color: 'rgba(255, 255, 255, 0.6)'
                              }}>Volume</div>
                            </div>
                            
                            <div style={{
                              background: 'rgba(17, 17, 23, 0.8)',
                              padding: '8px',
                              borderRadius: '6px',
                              textAlign: 'center'
                            }}>
                              <div style={{
                                fontSize: '15px',
                                fontWeight: '700',
                                color: 'white'
                              }}>{deepAnalysisData.tokenMetrics.uniqueAddresses}</div>
                              <div style={{
                                fontSize: '10px',
                                color: 'rgba(255, 255, 255, 0.6)'
                              }}>Addresses</div>
                            </div>
                          </div>
                          
                          <div style={{
                            fontSize: '11px',
                            color: 'rgba(255, 255, 255, 0.7)',
                            marginTop: '8px',
                            textAlign: 'center'
                          }}>
                            Period: {deepAnalysisData.tokenMetrics.transferPeriod}
                          </div>
                        </div>
                        
                        {/* Market Context Section */}
                        <div style={{
                          background: 'rgba(17, 17, 23, 0.97)',
                          padding: '12px'
                        }}>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#F59E0B',
                            marginBottom: '8px',
                            borderBottom: '1px solid rgba(245, 158, 11, 0.2)',
                            paddingBottom: '4px'
                          }}>Market Context</div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '8px'
                          }}>
                            <div style={{
                              background: 'rgba(0, 0, 0, 0.2)',
                              padding: '8px',
                              borderRadius: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                color: deepAnalysisData.marketContext.volumeChange.startsWith('-') ? '#EF4444' : '#10B981'
                              }}>{deepAnalysisData.marketContext.volumeChange}%</div>
                              <div style={{
                                fontSize: '9px',
                                color: 'rgba(255, 255, 255, 0.6)'
                              }}>Volume Change</div>
                            </div>
                            
                            <div style={{
                              background: 'rgba(0, 0, 0, 0.2)',
                              padding: '8px',
                              borderRadius: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                color: 'white'
                              }}>{deepAnalysisData.marketContext.priceImpact}%</div>
                              <div style={{
                                fontSize: '9px',
                                color: 'rgba(255, 255, 255, 0.6)'
                              }}>Price Impact</div>
                            </div>
                            
                            <div style={{
                              background: 'rgba(0, 0, 0, 0.2)',
                              padding: '8px',
                              borderRadius: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                color: 'white'
                              }}>{deepAnalysisData.marketContext.liquidityRatio}×</div>
                              <div style={{
                                fontSize: '9px',
                                color: 'rgba(255, 255, 255, 0.6)'
                              }}>Liquidity Ratio</div>
                            </div>
                          </div>
                          
                          <a 
                            href={`https://etherscan.io/tx/${deepAnalysisData.latestTransfer.hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              background: 'rgba(59, 130, 246, 0.15)',
                              color: 'rgb(59, 130, 246)',
                              textDecoration: 'none',
                              marginTop: '10px'
                            }}
                          >
                            <ExternalLink size={10} />
                            <span>View Latest Transaction</span>
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            // </ProSection>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationContent;